import type { MouseEvent, ReactNode } from "react";
import { ArrowRight, Mail } from "lucide-react";
import { useWaitlistSignup } from "@/lib/waitlist";

// Pressable controls for the marketing site: our octagon's chamfered corners, a darker lip along the
// bottom edge, and a soft shadow beneath, so a button reads as a key you can press. It lifts on hover
// and sinks onto its lip when pressed.

export const chamfer = (cut: number) =>
  `polygon(${cut}px 0, calc(100% - ${cut}px) 0, 100% ${cut}px, 100% calc(100% - ${cut}px), calc(100% - ${cut}px) 100%, ${cut}px 100%, 0 calc(100% - ${cut}px), 0 ${cut}px)`;

const TONES = {
  primary: {
    edge: "bg-[#00283a]",
    face: "bg-logo text-white group-hover:bg-[#0a4f6c]",
    lip: "shadow-[inset_0_-3px_0_rgba(0,20,30,0.45),inset_0_1px_0_rgba(255,255,255,0.14)] group-active:shadow-[inset_0_-1px_0_rgba(0,20,30,0.45),inset_0_1px_0_rgba(255,255,255,0.14)]",
    shadow: "drop-shadow(0 4px 8px rgba(0,40,58,0.22))",
  },
  secondary: {
    edge: "bg-slate-300",
    face: "bg-white text-slate-900 group-hover:bg-slate-50",
    lip: "shadow-[inset_0_-3px_0_#e2e8f0] group-active:shadow-[inset_0_-1px_0_#e2e8f0]",
    shadow: "drop-shadow(0 2px 4px rgba(15,23,42,0.08))",
  },
};

const SIZES = {
  sm: { face: "h-9 px-4 pb-0.5 text-sm", cut: 3 },
  lg: { face: "h-12 px-5 pb-0.5 text-[15px]", cut: 4 },
  xl: { face: "h-14 px-7 pb-0.5 text-base", cut: 5 },
};

export function PressButton({ href, onClick, type = "button", tone = "primary", size = "sm", disabled = false, className = "", children }: {
  href?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  type?: "button" | "submit";
  tone?: keyof typeof TONES;
  size?: keyof typeof SIZES;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const t = TONES[tone];
  const s = SIZES[size];
  const body = (
    <span className={`block w-full p-px ${t.edge}`} style={{ clipPath: chamfer(s.cut) }}>
      <span
        className={`flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-[background-color,box-shadow] duration-150 ${s.face} ${t.face} ${t.lip}`}
        style={{ clipPath: chamfer(s.cut - 0.4) }}
      >
        {children}
      </span>
    </span>
  );
  const shell = `group inline-flex transition-transform duration-150 ease-out hover:-translate-y-px active:translate-y-px disabled:pointer-events-none disabled:opacity-60 ${className}`;
  const style = { filter: t.shadow };
  return href ? (
    <a href={href} onClick={onClick} className={shell} style={style}>
      {body}
    </a>
  ) : (
    <button type={type} onClick={onClick} disabled={disabled} className={shell} style={style}>
      {body}
    </button>
  );
}

const BAR_SIZES = {
  md: { input: "h-11 text-[16px]", icon: "h-5 w-5", button: "lg" as const, pad: "p-1.5", cut: 5 },
  xl: { input: "h-14 text-lg", icon: "h-6 w-6", button: "xl" as const, pad: "p-2", cut: 6 },
};

/** Scroll to a signup and put the cursor in its email field. Call it from the click itself: iOS
 *  only opens the keyboard for a focus made during a tap, so the focus comes first and the scroll
 *  glides there after. */
export function goToSignup(e: MouseEvent<HTMLElement>, sectionId: string, inputId: string) {
  const section = document.getElementById(sectionId);
  const input = document.getElementById(inputId);
  if (!section || !(input instanceof HTMLInputElement)) return;
  e.preventDefault();
  input.focus({ preventScroll: true });
  const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
  section.scrollIntoView({ behavior, block: "start" });
  history.replaceState(null, "", `#${sectionId}`);
}

/** The signup: one raised field holding the email input and its button. On a phone the button sits
 *  under the input, still inside the field. */
export function SignupBar({ id, size = "md", placeholder = "Work email", buttonLabel = "See my facilities" }: {
  id?: string;
  size?: keyof typeof BAR_SIZES;
  placeholder?: string;
  buttonLabel?: string;
}) {
  const { email, change, submit, isSubmitted, isLoading, error } = useWaitlistSignup();
  const z = BAR_SIZES[size];
  if (isSubmitted) {
    return (
      <div className="bg-white px-5 py-4 text-left" style={{ clipPath: chamfer(z.cut), filter: "drop-shadow(0 10px 24px rgba(15,40,60,0.14))" }}>
        <p className="font-semibold text-slate-900">Thank you!</p>
        <p className="mt-1 text-sm text-slate-600">We'll be in touch about Octerm for your facilities.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="group/bar">
      <div style={{ filter: "drop-shadow(0 12px 24px rgba(15,40,60,0.13)) drop-shadow(0 2px 4px rgba(15,40,60,0.08))" }}>
        <div className="bg-slate-200 p-px transition-colors group-focus-within/bar:bg-logo/60" style={{ clipPath: chamfer(z.cut) }}>
          <div className={`flex flex-col gap-1.5 bg-white shadow-[inset_0_-3px_0_#eef2f5] sm:flex-row sm:items-center ${z.pad}`} style={{ clipPath: chamfer(z.cut - 0.4) }}>
            <label className="flex min-w-0 flex-1 items-center gap-3 px-3">
              <Mail className={`shrink-0 text-slate-400 transition-colors group-focus-within/bar:text-logo ${z.icon}`} aria-hidden />
              <span className="sr-only">Work email</span>
              <input
                id={id}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => change(e.target.value)}
                placeholder={placeholder}
                className={`w-full min-w-0 bg-transparent text-slate-900 outline-none placeholder:text-slate-400 ${z.input}`}
              />
            </label>
            <PressButton type="submit" size={z.button} disabled={isLoading} className="w-full sm:w-auto">
              {isLoading ? "Sending…" : buttonLabel}
              {!isLoading && <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden />}
            </PressButton>
          </div>
        </div>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </form>
  );
}
